import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { verifyEmail } from '@/services/authClient';

export default function VerifyEmail() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const startedRef = useRef(false);
  const succeededRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) return;
    startedRef.current = true;

    verifyEmail(token)
      .then(() => {
        succeededRef.current = true;
        setStatus('success');
      })
      .catch((err) => {
        if (succeededRef.current) return;
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.token?.[0] ||
          'Verification failed. The link may be invalid or expired.';
        setMessage(msg);
        setStatus('error');
      });
  }, [token]);

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-6">
      <div className="w-full max-w-sm text-center">
        {status === 'loading' && (
          <>
            <Loader2 size={48} className="mx-auto animate-spin text-blue-600" />
            <p className="mt-4 text-slate-600">Verifying your email…</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Email verified!</h1>
            <p className="mt-2 text-slate-600">Your account is now active. You can sign in.</p>
            <Button asChild className="mt-6 w-full rounded-xl">
              <Link to="/login">Go to login</Link>
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle size={48} className="mx-auto text-red-500" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Verification failed</h1>
            <p className="mt-2 text-slate-600">{message}</p>
            <Button asChild variant="outline" className="mt-6 w-full rounded-xl">
              <Link to="/login">Go to login</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
